'use client';

/**
 * DesignShowcase — the live half of the /design colophon.
 *
 * This is one of the theme engine's two sanctioned runtime switch surfaces on
 * the site. This showcase moves the identity (and previews a cut); the chrome
 * cut chooser in the header moves the visitor's cut. Every other DCYFR surface
 * pins its identity at build time via data-identity on <html>. Widening this
 * set requires amending the dcyfr-theme-engine decision record (rei-workspace,
 * openspec). Do not copy this component elsewhere.
 *
 * The switch restamps <html data-identity> under the engine's .theme-swap
 * one-frame transition suppression, and restores the build pin on unmount so
 * navigating away always lands back on the site's real identity. Nothing is
 * persisted on purpose: the switch is a showroom control, not a preference.
 *
 * The cut is a different axis and a different owner. The site's identity
 * (obsidian) declares cuts, the server stamps the visitor's cut on <html
 * data-cut> from the dcyfr-cut cookie, and the header's chooser moves it.
 * The cut control here is a preview only: it restamps data-cut the same way
 * the chooser does, but writes no cookie, so a reload or the next page
 * serves the visitor's own cut again. A preview of slate or phosphor carries
 * no data-cut (identities without cuts carry none), switching back puts the
 * visitor's cut back (the preview is dropped, never the base cut blindly),
 * and leaving the page restores the cookie-derived cut.
 *
 * The cut gallery renders every cut side by side without stamping
 * data-cut on anything: each card carries that cut's resolved variables as
 * inline custom properties (src/lib/theme-cuts.ts, cutVars). The dark
 * overlay selector is compound on <html>, so a data-cut stamp on a card
 * would render the base cut in the dark scheme, and audit:sites treats any
 * stamp below <html> as a defect.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { cn } from '@/lib/utils';
import { TYPOGRAPHY, SPACING } from '@/lib/design-tokens';
import { Button } from '@/components/ui/button';
import { DcyfrButton } from '@/components/ui/dcyfr-button';
import { DcyfrCard, DcyfrCardContent } from '@/components/ui/dcyfr-card';
import { cutLabel } from '@/components/chrome/cut-chooser';
import { trackIdentitySwitch } from '@/lib/analytics';
import { cutVars, themeCuts, type CutScheme, type CutToken, type ThemeCut } from '@/lib/theme-cuts';
import { readCutCookie } from '@/lib/theme-cut-cookie';

type Identity = 'slate' | 'phosphor' | 'obsidian';

/** The identity layout.tsx stamps at build time. Restored on unmount. */
const BUILD_IDENTITY: Identity = 'obsidian';

const CUT_ATTRIBUTE = themeCuts.attribute;

const IDENTITIES: Array<{ id: Identity; label: string; blurb: string }> = [
  {
    id: 'obsidian',
    label: 'obsidian',
    blurb:
      'The stone identity this site ships: one near-black ground shared by five cuts, with colour held in the accent ramp, the field and the hairline, a sheen in the rainbow cut, and an edge light in each single-hue cut in dark. The chooser in the header keeps your cut; the cut control below previews one.',
  },
  {
    id: 'phosphor',
    label: 'phosphor',
    blurb: 'The editorial identity: warm paper, vermilion accents, serif headlines.',
  },
  {
    id: 'slate',
    label: 'slate',
    blurb:
      'The shadcn new-york heritage identity: cool neutrals, the secure blue, Geist throughout.',
  },
];

/**
 * Contract token groups, mirroring theme-contract.json v3 in the registry
 * (src/registry/new-york/styles/). Site-extension tokens (charts, sidebar,
 * syntax) are deliberately absent: they are scheme-keyed site locals, not part
 * of the identity contract, so they would not respond to the switch.
 */
const PAIRED_TOKENS: Array<{
  group: string;
  pairs: Array<{ bg: string; fg: string; note: string }>;
}> = [
  {
    group: 'Surfaces',
    pairs: [
      { bg: '--background', fg: '--foreground', note: 'page ground' },
      { bg: '--card', fg: '--card-foreground', note: 'raised panels' },
      { bg: '--popover', fg: '--popover-foreground', note: 'floating layers' },
      { bg: '--muted', fg: '--muted-foreground', note: 'quiet fills' },
      { bg: '--secondary', fg: '--secondary-foreground', note: 'secondary controls' },
    ],
  },
  {
    group: 'Brand',
    pairs: [
      { bg: '--primary', fg: '--primary-foreground', note: 'primary controls' },
      { bg: '--accent', fg: '--accent-foreground', note: 'interaction accent' },
    ],
  },
  {
    group: 'Status',
    pairs: [
      { bg: '--secure', fg: '--secure-foreground', note: 'security posture' },
      { bg: '--success', fg: '--success-foreground', note: 'confirmation' },
      { bg: '--warning', fg: '--warning-foreground', note: 'caution' },
      { bg: '--destructive', fg: '--destructive-foreground', note: 'danger' },
    ],
  },
];

/** The brand accent ramp: one value never serves both fills and text. */
const ACCENT_RAMP = ['--accent-400', '--accent-500', '--accent-600', '--accent-700'];

/** Line tokens rendered as outlines rather than fills. */
const LINE_TOKENS: Array<{ token: string; note: string }> = [
  { token: '--border', note: 'hairlines' },
  { token: '--input', note: 'field edges' },
  { token: '--ring', note: 'focus ring' },
];

/** Contract v3's field group: the one dramatic surface a cut may re-author. */
const FIELD_TOKENS: Array<{ token: string; note: string }> = [
  { token: '--field', note: 'field ground' },
  { token: '--field-foreground', note: 'text on the field' },
  { token: '--field-accent', note: 'accent on the field' },
];

/** The sheen: an image value, never a fill, drawn only by the engine's utilities. */
const SHEEN_TOKEN = '--sheen';

const TYPE_TOKENS: Array<{ token: string; label: string; specimen: string }> = [
  { token: '--font-display', label: 'display', specimen: 'Decode the noise.' },
  {
    token: '--font-body',
    label: 'body',
    specimen:
      'Three identities share one token contract, so a component written once renders natively in each.',
  },
  { token: '--font-mono', label: 'mono', specimen: 'npx shadcn add @dcyfr-labs/dcyfr-button' },
];

const RADIUS_TOKENS: Array<{ token: string; note: string }> = [
  { token: '--radius-sm', note: 'nested elements' },
  { token: '--radius', note: 'controls' },
  { token: '--radius-pill', note: 'badges' },
];

const VALUE_TOKENS = [
  ...PAIRED_TOKENS.flatMap((g) => g.pairs.flatMap((p) => [p.bg, p.fg])),
  ...ACCENT_RAMP,
  ...LINE_TOKENS.map((t) => t.token),
  ...FIELD_TOKENS.map((t) => t.token),
  SHEEN_TOKEN,
];

/** A cut's resolved variables, applied to one card as inline custom properties. */
type CutStyle = CSSProperties & Record<CutToken, string>;
type SwatchStyle = CSSProperties & { '--swatch': string };

/** The cut <html> carries right now, or null for the base cut. */
function readStampedCut(): string | null {
  return document.documentElement.getAttribute(CUT_ATTRIBUTE);
}

/** The scheme next-themes has stamped on <html>. */
function readScheme(): CutScheme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * The cut the dcyfr-cut cookie names, resolved the way the server resolves
 * it: a declared non-base cut, null for the base, an unknown or a malformed
 * value, and undefined when there is no cookie at all. Never throws: this
 * runs in the unmount cleanup below, where an exception would take the
 * whole client-side navigation down (src/lib/theme-cut-cookie.ts).
 */
function readCookieCut(): string | null | undefined {
  return readCutCookie(document.cookie);
}

/**
 * Restamp <html> under the engine's one-frame transition suppression. Only
 * the identity that declares cuts carries data-cut (engine, "The cut axis"),
 * so a preview of another identity drops the attribute and a return to the
 * site's identity puts the visitor's cut back. The base cut is the absence
 * of the attribute, never its name, so the served and the live DOM agree.
 */
function stamp(identity: Identity, cut: string | null) {
  const root = document.documentElement;
  root.classList.add('theme-swap');
  root.setAttribute('data-identity', identity);
  if (identity === BUILD_IDENTITY && cut) root.setAttribute(CUT_ATTRIBUTE, cut);
  else root.removeAttribute(CUT_ATTRIBUTE);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove('theme-swap'));
  });
}

export function DesignShowcase() {
  const [identity, setIdentity] = useState<Identity>(BUILD_IDENTITY);
  const [cut, setCut] = useState<string | null>(null);
  const [scheme, setScheme] = useState<CutScheme>('light');
  const [values, setValues] = useState<Record<string, string>>({});
  // The visitor's cut as last seen on <html> while the site's own identity
  // was showing: the server's stamp on arrival, then whatever the header's
  // chooser stamped since. This is what a preview must hand back.
  const visitorCut = useRef<string | null>(null);
  // The cut this control last stamped as a preview, or undefined when the
  // stamp on <html> is the visitor's own. The observer below uses it to tell
  // a preview restamp from one the header's chooser made.
  const previewCut = useRef<string | null | undefined>(undefined);
  const cutOptions = useRef(new Map<string, HTMLButtonElement>());
  const cutCaptionId = useId();

  const readValues = useCallback(() => {
    const styles = getComputedStyle(document.documentElement);
    const next: Record<string, string> = {};
    for (const token of VALUE_TOKENS) {
      next[token] = styles.getPropertyValue(token).trim();
    }
    setValues(next);
    setScheme(readScheme());
  }, []);

  // Resolved values re-read after every identity change, and after scheme
  // changes made by the site's own theme toggle (next-themes flips the .dark
  // class on <html>, which this component does not own).
  useEffect(() => {
    readValues();
  }, [identity, readValues]);

  useEffect(() => {
    visitorCut.current = readStampedCut();
    setCut(visitorCut.current);

    // <html> has three owners: next-themes (class), this switch (identity
    // and the cut preview) and the header's chooser (cut). Watch all three
    // so the values below and the remembered cut both follow what is
    // actually stamped.
    const observer = new MutationObserver(() => {
      if (document.documentElement.getAttribute('data-identity') === BUILD_IDENTITY) {
        const stamped = readStampedCut();
        if (previewCut.current === undefined || stamped !== previewCut.current) {
          // Not this control's preview: the chooser (or the server) owns it.
          previewCut.current = undefined;
          visitorCut.current = stamped;
        }
        setCut(stamped);
      }
      readValues();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-identity', CUT_ATTRIBUTE],
    });

    // Restore the build pin, and the visitor's cut with it, when the visitor
    // navigates away. The cookie wins when there is one (the chooser may
    // have written it during a preview, when the observer above stands
    // down); the remembered stamp covers a visitor whose browser refuses
    // cookies. Neither path stamps the base cut blindly, and a cut preview
    // never survives the page.
    return () => {
      observer.disconnect();
      const fromCookie = readCookieCut();
      stamp(BUILD_IDENTITY, fromCookie === undefined ? visitorCut.current : fromCookie);
    };
  }, [readValues]);

  const handleSwitch = (next: Identity) => {
    if (next === identity) return;
    // An identity change drops any cut preview: the site's identity comes
    // back wearing the visitor's own cut. The state follows the stamp now
    // rather than one observer microtask later.
    previewCut.current = undefined;
    stamp(next, visitorCut.current);
    setCut(visitorCut.current);
    setIdentity(next);
    trackIdentitySwitch(next);
  };

  // A preview restamp: the chooser's mechanism without the cookie. Nothing
  // is reported (cut_chosen is the chooser's event) and nothing persists.
  const handleCut = (next: ThemeCut, moveFocus: boolean) => {
    if (moveFocus) cutOptions.current.get(next)?.focus();
    const stampValue = next === themeCuts.base ? null : next;
    if (stampValue === cut) return;
    previewCut.current = stampValue;
    stamp(identity, stampValue);
    setCut(stampValue);
  };

  // WAI-ARIA radio group: arrows move the check with the focus and wrap;
  // Home and End jump. Enter and Space are the button's own click.
  const onCutKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const cuts = themeCuts.cuts;
    const index = Math.max(cuts.indexOf((cut ?? themeCuts.base) as ThemeCut), 0);
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % cuts.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + cuts.length) % cuts.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = cuts.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    handleCut(cuts[next], true);
  };

  // Only the identity that declares cuts gets the cut control.
  const declaresCuts = identity === themeCuts.identity;
  const selectedCut = cut ?? themeCuts.base;
  const showsCut = declaresCuts && cut;
  const sheenValue = values[SHEEN_TOKEN] || '';

  return (
    <div className={cn(SPACING.subsection)} data-testid="design-showcase">
      {/* Identity switcher */}
      <div
        role="group"
        aria-label="Switch the live identity"
        className="rounded-xl border border-primary/20 bg-primary/5 p-6 md:p-8 flex flex-col gap-4"
      >
        <div className="flex flex-wrap items-center gap-3">
          {IDENTITIES.map(({ id, label }) => (
            <Button
              key={id}
              size="sm"
              variant={identity === id ? 'default' : 'outline'}
              aria-pressed={identity === id}
              data-identity-option={id}
              onClick={() => handleSwitch(id)}
              className="font-mono"
            >
              {label}
            </Button>
          ))}
          <span className="text-xs text-muted-foreground font-mono" aria-live="polite">
            data-identity=&quot;{identity}&quot;
            {showsCut ? ` data-cut="${cut}"` : ''}
          </span>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
          {IDENTITIES.find(({ id }) => id === identity)?.blurb}
        </p>

        {/* Cut preview: rendered only while the selected identity declares cuts */}
        {declaresCuts && (
          <div className="flex flex-col gap-2" data-testid="cut-preview">
            <span id={cutCaptionId} className="text-xs text-muted-foreground">
              Preview a cut
            </span>
            <div
              role="radiogroup"
              aria-labelledby={cutCaptionId}
              className="flex flex-wrap items-center gap-x-1"
              onKeyDown={onCutKeyDown}
            >
              {themeCuts.cuts.map((option) => {
                const checked = option === selectedCut;
                const swatch: SwatchStyle = { '--swatch': themeCuts.swatches[option] };
                return (
                  <button
                    key={option}
                    ref={(node) => {
                      if (node) cutOptions.current.set(option, node);
                      else cutOptions.current.delete(option);
                    }}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    tabIndex={checked ? 0 : -1}
                    data-cut-option={option}
                    className="group touch-target gap-2 rounded-md px-2 hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    onClick={() => handleCut(option, false)}
                  >
                    <span
                      aria-hidden="true"
                      style={swatch}
                      className="block size-4 shrink-0 rounded-full bg-[var(--swatch)] forced-color-adjust-none ring-1 ring-border group-aria-checked:ring-2 group-aria-checked:ring-foreground group-aria-checked:ring-offset-2 group-aria-checked:ring-offset-background"
                    />
                    <span className="text-sm decoration-2 underline-offset-4 group-aria-checked:underline">
                      {cutLabel(option)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
          Both controls restamp every token on this page live and keep nothing: every DCYFR surface
          pins one identity at build time, and this page and the cut chooser in the header are the
          only two places the engine switches at runtime. The chooser keeps your cut in a cookie;
          the preview above does not, so a reload serves your own cut again.
        </p>
      </div>

      {/* Color pairs */}
      {PAIRED_TOKENS.map(({ group, pairs }) => (
        <section key={group} aria-label={`${group} tokens`} className="flex flex-col gap-3">
          <h3 className={cn(TYPOGRAPHY.h3.standard)}>{group}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pairs.map(({ bg, fg, note }) => (
              <div
                key={bg}
                className="rounded-xl border border-border/50 overflow-hidden flex flex-col"
              >
                <div
                  className="p-4 flex flex-col gap-1 min-h-20"
                  style={{ backgroundColor: `var(${bg})`, color: `var(${fg})` }}
                >
                  <span className="text-sm font-medium">{note}</span>
                  {/* Full foreground, no opacity: the pair is what the
                      contract audits, and an 80% blend of muted-foreground
                      on muted measured 3.54:1 on the a11y gate. */}
                  <span className="font-mono text-xs">
                    {fg} on {bg}
                  </span>
                </div>
                <div className="px-4 py-2 bg-card text-muted-foreground font-mono text-xs truncate">
                  {values[bg] || ' '}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Accent ramp */}
      <section aria-label="Brand accent ramp" className="flex flex-col gap-3">
        <h3 className={cn(TYPOGRAPHY.h3.standard)}>Accent ramp</h3>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Four steps so one accent value never serves both fills and text: filled controls take
          -600, text on paper takes -700, and -400 is the text-safe step on dark grounds. A cut
          re-authors this ramp; the surfaces above stay put.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {ACCENT_RAMP.map((token) => (
            <div key={token} className="rounded-xl border border-border/50 overflow-hidden">
              <div className="h-16" style={{ backgroundColor: `var(${token})` }} />
              <div className="px-3 py-2 bg-card font-mono text-xs text-muted-foreground truncate">
                {token}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Field group readout */}
      <section aria-label="Field and sheen tokens" className="flex flex-col gap-3">
        <h3 className={cn(TYPOGRAPHY.h3.standard)}>Field and sheen</h3>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Contract v3 adds one dramatic surface: the field, with its own foreground and accent, and
          a sheen the engine lays over it. A cut re-authors all four. Only the rainbow cut gives the
          sheen a pattern, in both schemes; red, blue and green carry an edge light of their own hue
          in dark and none in light, and black sets it to none in both. The sheen is an image value
          (a gradient stack, or none) rather than a colour, and only the engine&apos;s utilities
          draw it.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {FIELD_TOKENS.map(({ token, note }) => (
            <div key={token} className="rounded-xl border border-border/50 overflow-hidden">
              <div className="h-16" style={{ backgroundColor: `var(${token})` }} />
              <div className="px-3 py-2 bg-card flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">{note}</span>
                <span className="font-mono text-xs text-muted-foreground truncate">{token}</span>
                <span className="font-mono text-xs text-muted-foreground truncate">
                  {values[token] || ' '}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-border/50 bg-card px-4 py-3 flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">sheen</span>
          <span className="font-mono text-xs text-muted-foreground">{SHEEN_TOKEN}</span>
          <span
            className="font-mono text-xs text-muted-foreground truncate"
            title={sheenValue || undefined}
            data-testid="sheen-readout"
          >
            {sheenValue || ' '}
          </span>
        </div>
      </section>

      {/* Field and sheen on primitives */}
      <section aria-label="Field and sheen on primitives" className="flex flex-col gap-3">
        <h3 className={cn(TYPOGRAPHY.h3.standard)}>The field on real primitives</h3>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Three engine utilities and nothing else draw the sheen. A surface wearing{' '}
          <code className="font-mono text-xs">.field</code> paints the field ground and shows the
          sheen at rest; <code className="font-mono text-xs">.sheen-hover</code> reveals it on hover
          and focus; <code className="font-mono text-xs">.sheen-scroll</code> widens the layer and
          drifts it with the element&apos;s own view timeline, a pure CSS scroll-driven animation.
          Under black, and under red, blue and green in light, the sheen is none and these surfaces
          show the field alone. The field is its own container rather than a card: a card paints its
          own surface, and that surface outranks the field.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/*
            The field is a plain container, not a DcyfrCard. The engine's
            `.field` lives in @layer components, so a primitive that paints its
            own surface (`bg-card text-card-foreground`) keeps that surface and
            the field never shows; the contrast gate then judges the text
            against the card and reports the field as unpainted. Nothing with
            its own opaque ground goes inside a field for the same reason: the
            gate judges every text node in a field against --field itself.
          */}
          <div
            className="field sheen-scroll rounded-xl p-5 flex flex-col gap-2"
            data-testid="field-specimen"
          >
            <span className="text-sm font-medium">.field .sheen-scroll</span>
            <span className="font-mono text-xs">--field-foreground on --field</span>
            <div className="flex items-center gap-2 mt-1">
              <span
                aria-hidden="true"
                className="block size-4 rounded-full"
                style={{ backgroundColor: 'var(--field-accent)' }}
              />
              <span className="font-mono text-xs">--field-accent</span>
            </div>
          </div>
          <DcyfrCard padding="md" className="justify-center">
            <DcyfrCardContent className="flex flex-col items-start gap-3">
              <DcyfrButton
                variant="ghostly"
                className="sheen-hover"
                data-testid="sheen-hover-button"
              >
                .sheen-hover
              </DcyfrButton>
              <span className="font-mono text-xs text-muted-foreground">
                hover or focus to reveal --sheen
              </span>
            </DcyfrCardContent>
          </DcyfrCard>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
          Reduced motion removes animation, not information: with the preference set, the field
          keeps its static sheen, the hover reveal happens without a transition, and the scroll
          drift never attaches. Browsers without scroll timelines get the static, centred layer.
        </p>
      </section>

      {/* Every cut side by side */}
      {declaresCuts && (
        <section aria-label="The five cuts" className="flex flex-col gap-3">
          <h3 className={cn(TYPOGRAPHY.h3.standard)}>Five cuts, one identity</h3>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
            Every cut of {themeCuts.identity} at once, in the current scheme. Each cut re-authors
            only the accent ramp, the field group, the hairline and the sheen, and only the rainbow
            cut gives the sheen a pattern (the contract would also allow a cut its muted tones; none
            of these takes that); every surface and status token below is shared. Each card carries
            its cut&apos;s resolved variables inline, generated from the vendored theme file at sync
            time, so nothing here stamps <code className="font-mono text-xs">data-cut</code>: that
            attribute belongs to the root element alone.
          </p>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="cut-gallery"
          >
            {themeCuts.cuts.map((option) => {
              const style: CutStyle = { ...cutVars[option][scheme] };
              const swatch: SwatchStyle = { '--swatch': themeCuts.swatches[option] };
              return (
                <div
                  key={option}
                  style={style}
                  className="field rounded-xl p-5 flex flex-col gap-3"
                  data-testid={`cut-sample-${option}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      style={swatch}
                      className="block size-4 shrink-0 rounded-full bg-[var(--swatch)] forced-color-adjust-none ring-1 ring-border"
                    />
                    <span className="text-sm font-medium">{cutLabel(option)}</span>
                    {option === themeCuts.base && <span className="font-mono text-xs">(base)</span>}
                  </div>
                  <div className="flex gap-1" aria-hidden="true">
                    {ACCENT_RAMP.map((token) => (
                      <span
                        key={token}
                        className="h-6 flex-1 rounded-sm"
                        style={{ backgroundColor: `var(${token})` }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="block size-3 rounded-full"
                      style={{ backgroundColor: 'var(--field-accent)' }}
                    />
                    <span className="font-mono text-xs">--field-accent</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Lines */}
      <section aria-label="Line tokens" className="flex flex-col gap-3">
        <h3 className={cn(TYPOGRAPHY.h3.standard)}>Lines</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {LINE_TOKENS.map(({ token, note }) => (
            <div
              key={token}
              className="rounded-xl bg-card p-4 flex flex-col gap-1"
              style={{ border: `2px solid var(${token})` }}
            >
              <span className="text-sm font-medium text-foreground">{note}</span>
              <span className="font-mono text-xs text-muted-foreground">{token}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Type */}
      <section aria-label="Type tokens" className="flex flex-col gap-3">
        <h3 className={cn(TYPOGRAPHY.h3.standard)}>Type</h3>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Themes supply faces through three role tokens; the engine binds the roles. Every identity
          reads in Geist. Phosphor reserves its serif for headlines, and the swap costs nothing here
          because the showcase loads every identity&apos;s faces.
        </p>
        <div className="flex flex-col gap-4">
          {TYPE_TOKENS.map(({ token, label, specimen }) => (
            <div
              key={token}
              className="rounded-xl border border-border/50 bg-card p-5 flex flex-col gap-2"
            >
              <span className="font-mono text-xs text-muted-foreground">
                {label} · {token}
              </span>
              <span
                className={cn(label === 'display' ? 'text-2xl font-bold' : 'text-base')}
                style={{ fontFamily: `var(${token})` }}
              >
                {specimen}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Radius + motion */}
      <section aria-label="Radius and motion tokens" className="flex flex-col gap-3">
        <h3 className={cn(TYPOGRAPHY.h3.standard)}>Radius &amp; motion</h3>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
          Radius styles interface chrome only; imagery and media stay square at the component layer.
          Motion ships as two duration-plus-easing fragments: snap for transform and opacity, ease
          for color and layout.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border/50 bg-card p-5 flex items-end gap-4">
            {RADIUS_TOKENS.map(({ token, note }) => (
              <div key={token} className="flex flex-col items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-full h-12 border-2 border-primary/40 bg-muted"
                  style={{ borderRadius: `var(${token})` }}
                />
                <span className="font-mono text-[10px] text-muted-foreground truncate max-w-full">
                  {note}
                </span>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border/50 bg-card p-5 flex flex-col justify-center gap-3">
            <button
              type="button"
              className="self-start rounded-md bg-primary text-primary-foreground text-sm px-4 py-2 hover:-translate-y-0.5 motion-reduce:transform-none"
              style={{ transition: `transform var(--motion-snap)` }}
            >
              --motion-snap
            </button>
            <button
              type="button"
              className="self-start rounded-md border border-border text-sm px-4 py-2 text-foreground hover:bg-muted"
              style={{ transition: `background-color var(--motion-ease)` }}
            >
              --motion-ease
            </button>
            <span className="font-mono text-[10px] text-muted-foreground">
              hover to feel the difference
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
